<script setup lang="ts">
import { useSubscription } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { ref, onMounted, nextTick, watch, computed } from "vue";
import { useRoute } from "vue-router";

import GlobalLoader from "./GlobalLoader.vue";

import { useToast } from "@/composables/useToast.ts";
import { useTour } from "@/composables/useTour.ts";
import { useAuthStore } from "@/store/auth/index.ts";
import { useBrandsStore } from "@/store/brands/index.ts";
import { useChannelsStore } from "@/store/channels/index.ts";
import { useCountriesStore } from "@/store/countries/index.ts";
import { useDeliveryStore } from "@/store/delivery/index.ts";
import { useFiltersStore } from "@/store/filters/index.ts";
import { usePrepStore } from "@/store/preparation/index.ts";
import { usePrintersStore } from "@/store/printers/index.ts";
import { useRetailersStore } from "@/store/retailers/index.js";
import { useWarehousesStore } from "@/store/warehouses/index.js";
import { IUsedPrinter } from "@/types/printers.js";
import { groupByLabelTitle } from "@/utils/formatters.ts";
import { registerTeleport } from "@/utils/modal.ts";

import CommonSidebar from "@/components/common/CommonSidebar.vue";
import AppHeader from "@/components/core/AppHeader/AppHeader.vue";
import AppNavSidebar from "@/components/core/NavBar/AppNavSidebar.vue";
import UnusedPrintersList from "@/components/printers/UnusedPrintersList.vue";

import "intro.js/minified/introjs.min.css";

const route = useRoute();
const { success } = useToast();
const { startTour } = useTour();
const prepStore = usePrepStore();
const authStore = useAuthStore();
const brandStore = useBrandsStore();
const filterStore = useFiltersStore();
const printersStore = usePrintersStore();
const channelsStore = useChannelsStore();
const deliveryStore = useDeliveryStore();
const countriesStore = useCountriesStore();
const retailersStore = useRetailersStore();
const warehousesStore = useWarehousesStore();
const printerSidebar = registerTeleport("printer-sidebar");

const updateLoader = ref(false);
const miniMenu = ref(true);
const isShowMenu = ref(false);
const isFixed = ref(false);
const area = ref("unknown");
/*
 * Show or hide the sidebar
 * @param {boolean} smallMenu
 */
const showMenu = (smallMenu: boolean) => {
  miniMenu.value = smallMenu;
  filterStore.handleNavMenu(smallMenu);
};

const showMobileMenu = (mobileMenu: boolean) => {
  filterStore.handleMobileMenu(mobileMenu);
  filterStore.handleNavMenu(false);
  miniMenu.value = false;
};

/*
 * Handle the small menu
 * @param {boolean} smallMenu
 */
const handleSmallMenu = (smallMenu: boolean) => {
  miniMenu.value = smallMenu;
  filterStore.handleNavMenu(smallMenu);
  filterStore.handleMobileMenu(false);
};

const loader = ref(true);

const search = ref("");
const printers = computed(() => {
  const usedPrinters: IUsedPrinter[] = printersStore.printers
    .filter((printer) => !!printer.printer)
    .map((printer) => ({
      id: printer.printer.id,
      name: printer.printer.name,
      title: printer.printer.title,
      type: printer.printer.type,
      active: true,
      labels: [
        {
          is_default: printer.printer.is_default,
          title: printer.label.title,
          name: printer.label.name,
          id: printer.label.id,
          type: printer.label.type,
          program: printer.label.program,
        },
      ],
    }));

  if (search.value) {
    const filteredPrinters = printersStore.unusedPrinters.filter((printer) => {
      const isPrinterTitle = printer?.title?.toLowerCase()?.includes(search.value.toLowerCase());
      const isPrinterName = printer?.name?.toLowerCase()?.includes(search.value.toLowerCase());
      const isPrinterType = printer?.type?.toLowerCase()?.includes(search.value.toLowerCase());
      const isPrinterProgram = printer?.labels?.some((label: any) => label?.program?.toLowerCase()?.includes(search.value.toLowerCase()));

      return isPrinterTitle || isPrinterName || isPrinterType || isPrinterProgram;
    });
    return groupByLabelTitle(filteredPrinters, usedPrinters);
  }
  return groupByLabelTitle(printersStore.unusedPrinters, usedPrinters);
});

const updateCallback = async () => {
  updateLoader.value = true;
  try {
    await printersStore.getUnusedPrinters(authStore.user?.id as string);
    if (route.path.includes("packaging") || route.path.includes("prep")) {
      area.value = "preparation";
      await Promise.allSettled([prepStore.fetchActualPrinter()]);
    }
    if (
      route.path.includes("delivery")
      || route.path.includes("purchase-order")
    ) {
      area.value = "receive_delivery";
      await Promise.allSettled([deliveryStore.fetchActualPrinters()]);
    }
  }
  catch (error) {
    console.log(error);
  }
  finally {
    updateLoader.value = false;
  }
};

const setPrinter = (printerId: string, labelId: string) => {
  printersStore.setToUser(
    authStore.user?.id as string,
    printerId,
    labelId,
    updateCallback,
  );
};

const { result, loading, error } = useSubscription(
  gql`
    subscription printFinished($userId: ID!) {
      printFinished(userId: $userId) {
        title
        message
        hasError
      }
    }
  `,
  { userId: authStore.user?.id },
);

watch(
  () => [result, loading, error],
  () => {
    if (result.value) {
      success("Print finished");
    }
  },
  {
    deep: true,
  },
);

watch(
  () => [printersStore.isOpenPrinterSideBar, printersStore.printers],
  () => {
    if (printersStore.isOpenPrinterSideBar) {
      printerSidebar.open();
    }
    else {
      printerSidebar.close();
    }
  },
  {
    deep: true,
  },
);

watch(
  () => [route.meta, authStore.user],
  () => {
    if (route.meta.printersList && authStore.user?.id) {
      updateCallback();
    }
  },
  {
    immediate: true,
    deep: true,
  },
);

onMounted(async () => {
  loader.value = true;
  try {
    await Promise.allSettled([
      // authStore.getMe(),
      brandStore.fetch(),
      channelsStore.fetchAll(),
      countriesStore.fetchAll(),
      retailersStore.fetchActiveRetailers(),
      warehousesStore.fetchAllWarehouses(),
    ]);
  }
  catch (error) {
    console.log(error);
  }
  finally {
    loader.value = false;
  }

  await nextTick();

  await startTour();
});
</script>

<template>
  <section>
    <AppHeader
      class="ml-4 md:ml-[100px]"
      :loading="loader"
    >
      <template #left>
        <div class="block md:hidden">
          <feather-icon
            type="menu"
            class="cursor-pointer hover:text-primary
            duration-500 transition-all"
            @click="showMobileMenu(true)"
          />
        </div>
      </template>
    </AppHeader>
    <main
      class="min-h-[calc(100vh-100px)] flex transition-all
      duration-500 overflow-x-hidden md:overflow-x-auto"
    >
      <AppNavSidebar
        class="tour-nav-bar"
        :is-open="isShowMenu"
        :is-small-menu="miniMenu"
        :mobile-menu="filterStore.isShowMobileMenu"
        @show-menu="showMenu"
        @handle-small-menu="handleSmallMenu"
      />
      <CommonSidebar
        :teleport="printerSidebar"
        title="Printers preference"
        @close="printersStore.openPrinterSideBar(false)"
      >
        <UnusedPrintersList
          v-model:search="search"
          :printers="printers"
          :area="area"
          :loader="updateLoader"
          @use-printer="setPrinter"
        />
      </CommonSidebar>

      <section
        v-if="!loader"
        class="pl-4 pb-5 pr-2 pt-0 w-full
        md:max-w-[calc(100vw-110px)] ml-[0] md:ml-[100px]"
        :class="{
          'mt-[100px]': isFixed,
        }"
      >
        <div class="pl-[15px] h-full tour-content">
          <router-view />
        </div>
      </section>
      <section
        v-else
        class="w-full h-[calc(100vh-200px)]"
      >
        <GlobalLoader height="calc(100vh - 200px)" />
      </section>
    </main>
  </section>
</template>
